import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

function getLangFromPath() {
  const path = window.location.pathname;
  if (path.startsWith("/pt")) return "pt";
  if (path.startsWith("/de")) return "de";
  return "en";
}

function getBasePath() {
  const path = window.location.pathname;
  if (path.startsWith("/pt")) return "/pt";
  if (path.startsWith("/de")) return "/de";
  return "";
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const lang = getLangFromPath();
  const basePath = getBasePath();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    window.scrollTo(0, 0);
  }, [lang, slug]);

  const article = useMemo(() => {
    const list = articles[lang as keyof typeof articles] || articles.en;
    return list.find((a) => a.slug === slug);
  }, [lang, slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-4">
            Article Not Found
          </h1>
          <a
            href={basePath || "/"}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            {t("article.back_to_home")}
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <div className="relative h-[280px] md:h-[400px] overflow-hidden">
        <img
          src={article.heroImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <nav className="mb-4" aria-label="breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-background-50/80">
              <li>
                <a href={basePath || "/"} className="hover:text-background-50 transition-colors">
                  {t("breadcrumbs.home")}
                </a>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-background-50/60">{article.category}</li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-background-50/90">{article.title}</li>
            </ol>
          </nav>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 max-w-3xl leading-tight">
            {article.title}
          </h1>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto -mt-6 relative z-20">
        <div className="bg-background-50 rounded-lg border border-background-200/50 p-5 md:p-6 shadow-sm mb-8">
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {t("article.author")} {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {t("article.published")} {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line"></i>
              {t("article.updated")} {article.updated}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime} {t("article.read_time")}
            </span>
            <span className="text-xs font-medium text-accent-700 bg-accent-100/60 px-2 py-0.5 rounded">
              {article.category}
            </span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          <aside className="lg:w-64 shrink-0">
            <div className="sticky top-24 bg-background-100 rounded-lg border border-background-200/40 p-4 md:p-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-4">
                {t("article.table_of_contents")}
              </h3>
              <ul className="space-y-2">
                {article.sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors leading-snug"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-4 border-t border-background-200/40">
                <a
                  href={basePath || "/"}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line text-xs"></i>
                  {t("article.back_to_home")}
                </a>
              </div>
            </div>
          </aside>

          <article className="flex-1 min-w-0">
            <div className="bg-background-50 rounded-lg border border-background-200/40 p-5 md:p-8">
              {article.sections.map((section, idx) => (
                <div key={section.id} id={section.id} className={idx > 0 ? "mt-8 md:mt-10 pt-8 md:pt-10 border-t border-background-200/30" : ""}>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-4">
                    {section.title}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}

              <div className="mt-10 pt-8 border-t border-background-200/30">
                <div className="bg-accent-50/50 rounded-lg border border-accent-200/40 p-4 md:p-5">
                  <p className="text-sm text-foreground-700 mb-4">
                    {t("final.description")}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="bg-primary-500 text-background-50 px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {t("hero.cta_primary")}
                    </a>
                    <a
                      href={basePath || "/"}
                      className="border border-foreground-300/40 text-foreground-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {t("article.back_to_home")}
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-background-200/30">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">
                  {t("article.related_articles")}
                </h3>
                <div className="flex items-center gap-2 p-3 bg-background-100 rounded-lg border border-background-200/30">
                  <a
                    href={basePath || "/"}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    CSSBuy Spreadsheet 2026: Complete Guide
                  </a>
                  <span className="text-xs text-foreground-500 ml-auto">{article.category}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <a
                href={basePath || "/"}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                {t("article.back_to_home")}
              </a>
            </div>
          </article>
        </div>
      </div>

      <div className="mt-16">
        <Footer />
      </div>
    </div>
  );
}